/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Button, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import { FC, Fragment } from "react";
import awvIcon from "./assets/icon_AWV.svg";
import { CameraSelector } from "./CameraSelector";
import { Const } from "const";
import { useTranslation } from "react-i18next";

interface Props {
  currentCameraNo?: number;
  cameraName?: string | null;
  onCameraChanged?: (cameraNo: number) => void;
  onBackClicked?: () => void;
}

/**
 * Create string for title
 * @param{number} cameraNo Camera No
 * @param{string} cameraName Camera name
 * @return{string} Title string
 */
const createCameraTitle = (cameraNo?: number, cameraName?: string | null) => {
  if (cameraNo !== undefined && cameraName) {
    return `${String(cameraNo).padStart(2, "0")} ${cameraName}`;
  }
};

/**
 * Component of camera controller
 * @param{Props} param0 Properties
 * @return{FC} Component
 */
export const ControlPanel: FC<Props> = ({
  currentCameraNo,
  cameraName,
  onCameraChanged,
  onBackClicked,
}: Props) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <Container>
        <TitleArea>
          <Typography variant="h5">
            {createCameraTitle(currentCameraNo, cameraName)}
          </Typography>
        </TitleArea>
        <OperationArea>
          <ButtonContainer>
            <Box
              sx={{
                gridRow: 2,
                gridColumn: "2/4",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={awvIcon} alt="awv" width={"80%"} />
            </Box>
            <Box
              sx={{
                gridRow: 1,
                gridColumn: "1/3",
                display: "flex",
                justifyContent: "right",
              }}
            >
              <CameraSelector
                isCurrent={currentCameraNo === Const.cameraFrontLeft}
                cameraNo={Const.cameraFrontLeft}
                position="FrontLeft"
                onClick={() => {
                  if (onCameraChanged) onCameraChanged(Const.cameraFrontLeft);
                }}
              />
            </Box>
            <Box sx={{ gridRow: 1, gridColumn: "3/5" }}>
              <CameraSelector
                isCurrent={currentCameraNo === Const.cameraFrontRight}
                cameraNo={Const.cameraFrontRight}
                position="FrontRight"
                onClick={() => {
                  if (onCameraChanged) onCameraChanged(Const.cameraFrontRight);
                }}
              />
            </Box>
            <Box
              sx={{
                gridRow: 2,
                gridColumn: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <CameraSelector
                isCurrent={currentCameraNo === Const.cameraLeft}
                cameraNo={Const.cameraLeft}
                position="Left"
                onClick={() => {
                  if (onCameraChanged) onCameraChanged(Const.cameraLeft);
                }}
              />
            </Box>
            <Box
              sx={{
                gridRow: 2,
                gridColumn: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <CameraSelector
                isCurrent={currentCameraNo === Const.cameraRight}
                cameraNo={Const.cameraRight}
                position="Right"
                onClick={() => {
                  if (onCameraChanged) onCameraChanged(Const.cameraRight);
                }}
              />
            </Box>
            <Box
              sx={{
                gridRow: 3,
                gridColumn: "1/5",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CameraSelector
                isCurrent={currentCameraNo === Const.cameraRear}
                cameraNo={Const.cameraRear}
                position="Rear"
                onClick={() => {
                  if (onCameraChanged) onCameraChanged(Const.cameraRear);
                }}
                width={"90%"}
                height={"90%"}
              />
            </Box>
          </ButtonContainer>
        </OperationArea>
        <FooterArea>
          <Button variant="outlined" color="inherit" onClick={onBackClicked}>
            {t("AWVUI-MSG95")}
          </Button>
        </FooterArea>
      </Container>
    </Fragment>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const TitleArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 2),
  height: 55,
  backgroundColor: Const.sidePanelHeaderColor,
  justifyContent: "flex-start",
  alignItems: "center",
}));

const FooterArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 2),
  height: 55,
  backgroundColor: Const.sidePanelFooterColor,
  justifyContent: "center",
  alignItems: "center",
}));

const OperationArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 2),
  flexGrow: 1,
  backgroundColor: Const.sidePanelBodyColor,
  justifyContent: "center",
  alignItems: "center",
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  padding: theme.spacing(1, 2),
}));
